import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useLocation } from 'react-router';
import { useUserData } from '../queries';
import { sanityImageURL } from '../helpers/sanity';
import { FEATURE_FLAG } from '../constants/featureFlags';
import { useMarketplaceConfig } from '../queries/marketplace';
import { useSkinStyles } from '../hooks/useSkinStyles';
import { useInstallPromptEnhancer } from '../hooks/useInstallPromptEnhancer';
import { SKIN } from '../types/enums';
import { getInstallPromptOverlayStyles } from '../styles/installPromptStyles';
import useResponsiveLogo from '../hooks/useResponsiveLogo';

// Feature-gates the install prompt via GrowthBook restrictedDomains config.
const preventPWA = (restrictedDomains: string[] = []): boolean => {
  const host = window.location.hostname;
  return restrictedDomains.some((domain) => host === domain || host.endsWith('.' + domain));
};

// CDN for the third-party add-to-homescreen package (v4.1.0).
const baseURL = 'https://cdn.jsdelivr.net/gh/philfung/add-to-homescreen@4.1.0/dist';
const getAppName = (fallback?: string) => document.title.trim() || fallback || 'Sportsbook';

type InstallPromptConfig = {
  maxModalDisplayCount: number;
  paths: string[];
  restrictedDomains: string[];
  supportedLanguages?: string[];
} | null;

type InstallPromptContextValue = {
  canShowInstallPrompt: boolean;
  showInstallPrompt: () => void;
  installPromptLocale: string;
};

const noop = () => undefined;

const InstallPromptContext = createContext<InstallPromptContextValue>({
  canShowInstallPrompt: false,
  showInstallPrompt: noop,
  installPromptLocale: 'en',
});

const useResolvedLocale = (config: InstallPromptConfig) => {
  const { data: userData } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();

  const defaultLang = marketplaceConfig?.marketplaceConfig?.languageConfig?.defaultLanguage;
  const locale = userData?.language ?? defaultLang;
  const supportedLocale = config?.supportedLanguages?.find((l) => l === locale) || 'en';

  return supportedLocale;
};

type InstallPromptProviderProps = {
  children: React.ReactNode;
};

/**
 * InstallPromptProvider
 *
 * Wraps the app shell and handles:
 * 1. Dynamic loading of the third-party add-to-homescreen CSS + JS.
 * 2. Creating the global AddToHomeScreenInstance with app name + logo.
 * 3. Auto-showing the prompt on configured paths (GrowthBook).
 * 4. Skin-aware overlay styling (only Neobet gets custom overrides).
 * 5. DOM enhancement (injects "install later" info text into the modal).
 *
 * Consumers use useInstallPrompt() to trigger the prompt manually (e.g. sidebar button).
 */
export const InstallPromptProvider = ({ children }: InstallPromptProviderProps) => {
  const { data: userData } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const location = useLocation();
  const [loaded, setLoaded] = useState(!!window.AddToHomeScreenInstance);
  const { resolvedImage } = useResponsiveLogo();
  const sanitySrc = sanityImageURL(resolvedImage, { h: 60 });
  const theme = useTheme();

  const config = useFeatureValue(FEATURE_FLAG.FULL_SCREEN_CONFIG, null) as InstallPromptConfig;
  const [instanceCreated, setInstanceCreated] = useState(!!window.AddToHomeScreenInstance);

  const pathName = location.pathname;
  const appName = getAppName(marketplaceConfig?.name);
  const resolvedLocale = useResolvedLocale(config);

  // Loads the third-party CSS + locale-specific JS from CDN.
  // Checks for existing tags to avoid redundant network requests on remount.
  useEffect(() => {
    if (preventPWA(config?.restrictedDomains)) {
      return;
    }

    const linkId = 'adhs-css';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `${baseURL}/add-to-homescreen.min.css`;
      document.head.appendChild(link);
    }

    const scriptId = `adhs-script-${resolvedLocale}`;
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (window.AddToHomeScreen && window.AddToHomeScreenLocale === resolvedLocale) {
      setLoaded(true);
      return;
    }

    setLoaded(false);

    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.defer = true;
      script.src = `${baseURL}/add-to-homescreen_${resolvedLocale}.min.js`;
      script.onload = () => {
        window.AddToHomeScreenLocale = resolvedLocale;
        setLoaded(true);
      };
      // eslint-disable-next-line no-console
      script.onerror = () => console.error('Failed to load script:', script.src);
      document.head.appendChild(script);
    } else if (window.AddToHomeScreen) {
      window.AddToHomeScreenLocale = resolvedLocale;
      setLoaded(true);
    } else {
      existingScript.onload = () => {
        window.AddToHomeScreenLocale = resolvedLocale;
        setLoaded(true);
      };
    }
  }, [resolvedLocale, config?.restrictedDomains]);

  // Creates the global instance once the script has loaded and we have a logo URL.
  // Avoids recreating if the instance already exists for the current locale.
  useEffect(() => {
    if (!loaded || !sanitySrc) return;

    if (window.AddToHomeScreen) {
      if (window.AddToHomeScreenInstance && window.AddToHomeScreenLocale === resolvedLocale) {
        setInstanceCreated(true);
        return;
      }

      window.AddToHomeScreenInstance = window.AddToHomeScreen({
        appName,
        appIconUrl: sanitySrc,
        maxModalDisplayCount: config?.maxModalDisplayCount ?? -1,
        assetUrl: `${baseURL}/assets/img/`,
      });
      window.AddToHomeScreenLocale = resolvedLocale;
      setInstanceCreated(true);
    }
  }, [appName, config?.maxModalDisplayCount, loaded, sanitySrc, resolvedLocale]);

  // Eligibility: config loaded, script ready, instance created, not standalone, not restricted.
  const canShowInstallPrompt = useMemo(() => {
    if (!config || !loaded || !instanceCreated) return false;
    if (preventPWA(config?.restrictedDomains)) return false;
    if (window.AddToHomeScreenInstance?.isStandAlone()) return false;

    return true;
  }, [config, loaded, instanceCreated]);

  // Triggers the third-party overlay. Falls back gracefully if the instance is missing.
  const showInstallPrompt = useCallback(() => {
    if (!canShowInstallPrompt) return;

    try {
      window.AddToHomeScreenInstance?.show(resolvedLocale);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error showing install prompt!');
    }
  }, [canShowInstallPrompt, resolvedLocale]);

  // Auto-show on first visit to configured paths (e.g. /sports) unless already displayed.
  useEffect(() => {
    if (!config || !loaded || preventPWA(config?.restrictedDomains)) {
      return;
    }

    const pathMatch = config?.paths?.some((path) => pathName.includes(path));
    const mounted = !!document.getElementsByClassName('adhs-container')?.length;

    const displayUI = pathMatch && !mounted;

    if (displayUI && instanceCreated) {
      showInstallPrompt();
    }
  }, [pathName, config, loaded, instanceCreated, showInstallPrompt]);

  const value = useMemo(
    () => ({
      canShowInstallPrompt,
      showInstallPrompt,
      installPromptLocale: resolvedLocale,
    }),
    [canShowInstallPrompt, showInstallPrompt, resolvedLocale]
  );

  // Injects the "install later" info text into the third-party modal (Neobet only).
  useInstallPromptEnhancer();

  // Skin-aware overlay CSS overrides. Only Neobet applies custom theme styling.
  const overlayStyles = useSkinStyles<Record<string, unknown>>({
    [SKIN.BET365]: {},
    [SKIN.NEOBET]: () => getInstallPromptOverlayStyles(theme),
  });

  return (
    <InstallPromptContext.Provider value={value}>
      <GlobalStyles styles={overlayStyles} />
      {children}
    </InstallPromptContext.Provider>
  );
};

export const useInstallPrompt = () => useContext(InstallPromptContext);
